<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-card>
        <v-card-title class="pb-0">
          <h1 class="headline font-weight-medium mb-2 mx-1">
            {{ header }}
          </h1>
          <ActionsModalWindow
            v-if="modal"
            :id="id"
            component-name="Service"
            @close="$emit('close')"
          />
        </v-card-title>
        <v-form>
          <v-card-text class="py-0">
            <v-row>
              <v-col class="pb-0">
                <CustomTextField
                  v-model="shortTitle"
                  error-count="3"
                  :error-messages="shortTitleValidationErrors"
                  :hide-details="false"
                  label="Коротке Найменування*"
                  required
                  @input="$v.shortTitle.$touch()"
                />
              </v-col>
              <v-col class="pb-0">
                <ServiceTypeSelect v-model="type" @input="isChanged = true" />
              </v-col>
              <v-col class="pb-0">
                <CustomTextField
                  v-model="article"
                  label="Артикул"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col class="pt-0">
                <CustomTextField
                  v-model="title"
                  label="Найменування"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="auto">
                <h2 class="subtitle-1 font-weight-medium mx-1">
                  Варіанти відвантаження*:
                </h2>
                <v-radio-group
                  v-model="saleOptions"
                  row
                  class="ml-1 mt-3 mb-n6"
                  @change="isChanged = true"
                >
                  <v-radio
                    label="Тільки з ПДВ"
                    :value="serviceSaleOptions.WITH_VAT.label"
                  />
                  <v-radio
                    label="Тільки без ПДВ"
                    :value="serviceSaleOptions.WITHOUT_VAT.label"
                  />
                  <v-radio
                    label="Можливо як з ПДВ, так і без ПДВ"
                    :value="serviceSaleOptions.BOTH_VAT.label"
                  />
                  <v-radio
                    label="Безкоштовно"
                    :value="serviceSaleOptions.FREE.label"
                  />
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row class="mb-n5">
              <v-col>
                <ServiceReccurenceSelect
                  v-model="recurrence"
                  class="mx-1"
                  label="Періодичність"
                  @change="isChanged = true"
                  @input="isChanged = true"
                />
              </v-col>
              <v-col>
                <DecimalEditor
                  v-model="amount"
                  :disabled="disableAmount"
                  :error-messages="$v.amount.$error ? errors.AMOUNT : []"
                  :scale="2"
                  :label="amountWithoutLabel"
                  class="mx-1"
                  :hide-details="false"
                  @input="$v.amount.$touch()"
                />
              </v-col>
              <v-col>
                <DecimalEditor
                  v-model="amountWithVAT"
                  :disabled="disableAmountWithVAT"
                  :error-messages="
                    $v.amountWithVAT.$error ? errors.AMOUNT_WITH_VAT : []
                  "
                  :scale="2"
                  :label="amountWithLabel"
                  :hide-details="false"
                  class="mx-1"
                  @input="$v.amountWithVAT.$touch()"
                />
              </v-col>

              <v-col>
                <DecimalEditor
                  v-model="purchasePrice"
                  :scale="2"
                  label="Ціна закупівлі*, грн"
                  :error-messages="$v.purchasePrice.$error ? errors.PRICE : []"
                  :hide-details="false"
                  @input="$v.purchasePrice.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-switch
                  v-model="accountAsCommodity"
                  label="Вести облік як товару"
                  class="ma-1 mb-n7 mt-n1"
                  color="green"
                  @change="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col class="pb-1">
                <v-textarea
                  v-model="comments"
                  label="Примітки"
                  class="mx-1 mt-1 mb-n3"
                  dense
                  outlined
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>
            <v-row v-if="!isNew">
              <v-col class="pt-0">
                <v-row class="ml-2">
                  <p class="title font-weight-bold mb-3">
                    Клієнти
                  </p>
                  <DownloadFile
                    location="xlsx"
                    :filename="`Клієнти сервіса ${shortTitle}`"
                    extension="xlsx"
                    :params="specificationData"
                    tooltip="Завантажити у форматі Excel"
                    :disabled="!clients.length"
                  />
                </v-row>
                <v-data-table
                  :headers="clientHeaders"
                  :items="clients"
                  flat
                  :loading="loading"
                  :sort-by="['payedTo']"
                  :sort-desc="[true]"
                  :footer-props="{
                    itemsPerPageText: 'Кількість рядків на сторінці:',
                  }"
                  :hide-default-footer="clients.length < 11"
                >
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>{{ item.id }}</td>
                      <td>
                        <ReferenceTitle
                          :id="item.id"
                          component-name="Client"
                          :title="item.title"
                        />
                      </td>
                      <td>{{ moment(item.payedTo).format("DD.MM.YY") }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions v-if="!modal">
            <PrimaryBtn
              :has-warnings="$v.$invalid"
              :loading="loading"
              @click="save"
            />
            <v-btn v-if="!modal" @click="$router.go(-1)">
              Відмінити
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import DecimalEditor from "@/components/DecimalEditor.vue";
import ServiceTypeSelect from "../service-types/ServiceTypeSelect.vue";
import ServiceReccurenceSelect from "@/components/enums/ServiceRecurrenceSelect.vue";
import PrimaryBtn from "@/components/PrimaryBtn.vue";
import ReferenceTitle from "@/components/ReferenceTitle.vue";
import DownloadFile from "../../../components/DowloadFile.vue";

import { ERRORS, hasTwoSpaces } from "@/helpers";
import * as servicesApi from "@/backend/servicesApi.js";
import serviceSaleOptions from "@/helpers/serviceSaleOptions.js";

import { required, minValue } from "vuelidate/lib/validators";
import { columnTypes, numberToString } from "@/helpers";

export default {
  components: {
    DecimalEditor,
    ServiceTypeSelect,
    ServiceReccurenceSelect,
    PrimaryBtn,
    ReferenceTitle,
    DownloadFile,
    ActionsModalWindow() {
      return import("@/components/ActionsModalWindow.vue");
    },
  },
  props: ["id", "modal"],
  data: () => ({
    title: "",
    shortTitle: "",
    article: "",
    recurrence: 1,
    amount: 0,
    amountWithVAT: 0,
    comments: "",
    type: null,
    loading: false,
    isChanged: false,
    saleOptions: null,
    purchasePrice: 0,
    accountAsCommodity: false,
    clients: [],
    initialShortTitle: "",
    shortTitleUnique: false,
    clientHeaders: [
      {
        text: "Id",
        value: "id",
        type: "text",
        sortable: false,
      },
      {
        text: "Найменування",
        value: "title",
        reference: true,
        componentName: "Client",
        getTitle: (title) => title,
        sortable: false,
      },
      {
        text: "Сплачено до",
        value: "payedTo",
        type: "text",
        sortable: true,
      },
    ],
  }),

  validations: {
    shortTitle: {
      required,
      isUnique() {
        return !this.shortTitleUnique;
      },
      hasTwoSpaces(value) {
        return hasTwoSpaces(value);
      },
    },
    purchasePrice: {
      minValue: minValue(0),
    },
    amount: {
      minValue(value) {
        return this.disableAmount || value > 0;
      },
    },
    amountWithVAT: {
      minValue(value) {
        return this.disableAmountWithVAT || value > 0;
      },
    },
  },

  computed: {
    isNew() {
      return +this.id ? false : true;
    },
    disableAmount() {
      return (
        this.saleOptions === this.serviceSaleOptions.WITH_VAT.label ||
        this.saleOptions === this.serviceSaleOptions.FREE.label
      );
    },
    disableAmountWithVAT() {
      return (
        this.saleOptions === this.serviceSaleOptions.WITHOUT_VAT.label ||
        this.saleOptions === this.serviceSaleOptions.FREE.label
      );
    },
    header() {
      if (this.modal || !this.isNew) {
        return "Сервіс";
      }
      return "Створення сервісу";
    },
    errors() {
      return {
        AMOUNT: [ERRORS.REQUIRED],
        TITLE: {
          HAS_TWO_SPACES: "Два пробіли не приймаються",
          IS_UNIQUE: "Сервіс вже існує",
          REQUIRED: ERRORS.REQUIRED,
        },
        AMOUNT_WITH_VAT: [ERRORS.REQUIRED],
        PRICE: [ERRORS.ONLY_POSITIVE],
      };
    },
    serviceSaleOptions() {
      return serviceSaleOptions;
    },
    amountWithLabel() {
      if (this.saleOptions === this.serviceSaleOptions.WITH_VAT.label) {
        return "Вартість, грн з ПДВ*";
      } else if (
        this.saleOptions === this.serviceSaleOptions.WITHOUT_VAT.label
      ) {
        return "Вартість, грн з ПДВ";
      } else {
        return "Вартість, грн з ПДВ*";
      }
    },
    amountWithoutLabel() {
      if (this.saleOptions === this.serviceSaleOptions.WITH_VAT.label) {
        return "Вартість, грн без ПДВ";
      } else if (
        this.saleOptions === this.serviceSaleOptions.WITHOUT_VAT.label
      ) {
        return "Вартість, грн без ПДВ*";
      } else {
        return "Вартість, грн без ПДВ*";
      }
    },
    shortTitleValidationErrors() {
      const errors = [];

      if (!this.$v.shortTitle.$dirty) return errors;

      const title = this.$v.shortTitle;

      if (!title.hasTwoSpaces) {
        errors.push(this.errors.TITLE.HAS_TWO_SPACES);
      }
      if (!title.isUnique) {
        errors.push(this.errors.TITLE.IS_UNIQUE);
      }
      if (!title.required) {
        errors.push(this.errors.TITLE.REQUIRED);
      }

      return errors;
    },
    specificationData() {
      return {
        rows: this.clients.map((item) => {
          return [numberToString(item.id), item.title, item.payedTo];
        }),
        columns: this.clientHeaders.map((h) => h.text),
        columnTypes: [columnTypes.STRING, columnTypes.STRING, columnTypes.DATE],
      };
    },
  },
  watch: {
    saleOptions: {
      handler(val) {
        if (val === this.serviceSaleOptions.WITH_VAT.label) {
          this.amount = 0;
        } else if (val === this.serviceSaleOptions.WITHOUT_VAT.label) {
          this.amountWithVAT = 0;
        } else if (val === this.serviceSaleOptions.FREE.label) {
          this.amount = 0;
          this.amountWithVAT = 0;
        } else if (this.isNew && val === null) {
          this.saleOptions = this.serviceSaleOptions.WITH_VAT.label;
        }
      },
      immediate: true,
    },
    shortTitle: {
      handler(val, oldVal) {
        if (val !== oldVal && val !== "") {
          this.checkShortTitleAsUniq(val);
        }
      },
    },
  },
  async created() {
    if (+this.id) {
      const service = await servicesApi.getService(this.id);
      this.title = service.title;
      this.shortTitle = service.shortTitle;
      this.initialShortTitle = service.shortTitle;
      this.article = service.article;
      this.saleOptions = service.saleOptions;
      this.recurrence = service.recurrence.id;
      this.amount = service.amount;
      this.purchasePrice = service.purchasePrice;
      this.amountWithVAT = service.amountWithVAT;
      this.comments = service.comments;
      this.accountAsCommodity = service.accountAsCommodity;
      this.type = service.type;
      this.clients = service.clients;
    }
  },
  methods: {
    async save() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.loading = true;

      const service = {
        title: this.title,
        shortTitle: this.shortTitle,
        article: this.article,
        saleOption: this.getServiceSaleOptionsType(),
        amount: this.amount,
        amountWithVAT: this.amountWithVAT,
        recurrence: this.recurrence,
        comments: this.comments,
        accountAsCommodity: this.accountAsCommodity,
        typeId: this.type?.id || null,
        purchasePrice: this.purchasePrice,
      };

      try {
        if (!this.isNew) {
          await servicesApi.updateService(this.id, service);
        } else {
          await servicesApi.createService(service);
        }

        this.$notifySuccess();
        this.modal ? this.$emit("close") : this.$router.go(-1);
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
    getServiceSaleOptionsType() {
      let saleOptions = "";
      for (const saleOption in serviceSaleOptions) {
        if (this.saleOptions === serviceSaleOptions[saleOption].label) {
          saleOptions = this.serviceSaleOptions[saleOption].value;
        }
      }
      return saleOptions;
    },
    async checkShortTitleAsUniq(value) {
      if (this.shortTitle === this.initialShortTitle) return;

      this.shortTitleUnique = await servicesApi.validateShortTitle(value);

      if (this.shortTitleUnique) {
        this.$notifyWarning("Сервіс вже існує");
      }
    },
  },
};
</script>

<style lang="scss">
.error--text .v-input__slot .v-text-field__slot input {
  color: inherit;
}
</style>
